import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { store } from './store';
import AppRoutes from './routes/AppRoutes';
import AuthManager from './components/auth/AuthManager';
import GlobalClickSpark from './components/common/GlobalClickSpark';
import SessionInvalidatedModal from './components/auth/SessionInvalidatedModal';
import ZoomControl from './components/common/ZoomControl';
import VibeDrawer from './components/common/VibeDrawer';
import FloatingAssistWidget from './components/common/FloatingAssistWidget';
import AssistToastHost from './components/common/AssistToastHost';
import { CandidateNavProvider } from './contexts/CandidateNavContext';
import { setNavigateRef } from './utils/navigationRef';
import MaintenanceRoutes from './routes/MaintenanceRoutes';
import { useAppDispatch, useAppSelector } from './store';
import { initializeAuth } from './store/authSlice';

// Import CSRF test utilities for development
import './utils/csrfTest';
import { CSRF_UTILS, MAINTENANCE_CONFIG, isAllowedBetaEmail } from './utils/constants';

// Make CSRF utilities available globally for console testing
if (process.env.NODE_ENV === 'development') {
  (window as any).CSRF_UTILS = CSRF_UTILS;
}

const NavigationSetter: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigateRef(navigate);
  }, [navigate]);
  return null;
};

const MaintenanceLoading: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '18px',
      color: '#98a0a8',
      background: '#1a1a1a',
    }}
  >
    Loading...
  </div>
);

const FullApp: React.FC = () => (
  <AuthManager>
    <CandidateNavProvider>
      <div className="App">
        <AppRoutes />
        <GlobalClickSpark
          sparkColor="#ffffff"
          sparkSize={8}
          sparkRadius={12}
          sparkCount={6}
          duration={300}
          easing="ease-out"
        />
        <SessionInvalidatedModal />
        <ZoomControl />
        <VibeDrawer />
        <FloatingAssistWidget />
        <AssistToastHost />
      </div>
    </CandidateNavProvider>
  </AuthManager>
);

const AppGate: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, isInitialized } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (MAINTENANCE_CONFIG.ENABLED && !isInitialized) {
      dispatch(initializeAuth());
    }
  }, [dispatch, isInitialized]);

  const isBetaBypass =
    isAuthenticated && !!user?.email && isAllowedBetaEmail(user.email);

  if (MAINTENANCE_CONFIG.ENABLED && !isBetaBypass) {
    if (!isInitialized) {
      return <MaintenanceLoading />;
    }

    return <MaintenanceRoutes />;
  }

  return <FullApp />;
};

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <NavigationSetter />
        <AppGate />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
